import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import ContractDragDropAssign from '../components/ContractDragDropAssign';

/**
 * Resources → Allocation (replaces the old allocation table).
 *
 * Drag employees from the left rail onto a contract / line-item / sub-item
 * on the right. Saves instantly to /api/contract-allocations.
 */
export default function ContractAllocations() {
  const [employees, setEmployees]       = useState([]);
  const [allocations, setAllocations]   = useState([]);
  const [contractTree, setContractTree] = useState([]);
  const [skillGroups, setSkillGroups]   = useState([]);
  const [skillsets, setSkillsets]       = useState([]);
  const [empSkillsMap, setEmpSkillsMap] = useState({});
  const [loading, setLoading]           = useState(true);
  const [err, setErr]                   = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [empRes, allocRes, treeRes, cascRes, skillsAllRes] = await Promise.all([
        apiFetch('/api/employees?limit=500').then(r => r.ok ? r.json() : { employees: [] }),
        apiFetch('/api/contract-allocations').then(r => r.ok ? r.json() : { allocations: [] }),
        apiFetch('/api/contract-allocations/tree').then(r => r.ok ? r.json() : { contracts: [] }),
        apiFetch('/api/metadata/cascade').then(r => r.ok ? r.json() : {}),
        apiFetch('/api/employees/skills/all').then(r => r.ok ? r.json() : { skills: [] }),
      ]);
      setEmployees(empRes.employees || []);
      setAllocations(allocRes.allocations || []);
      setContractTree(treeRes.contracts || []);
      setSkillGroups((cascRes.modules || []).map(m => ({ value: m.name, label: m.name })));
      setSkillsets((cascRes.skillsets || []).map(s => ({ value: s.name, label: s.name })));

      const map = {};
      (skillsAllRes.skills || []).forEach(s => {
        if (!map[s.employee_id]) map[s.employee_id] = new Set();
        const groupName = s.module_name || s.skill_category;
        if (groupName) map[s.employee_id].add(groupName);
      });
      const mapped = {};
      Object.entries(map).forEach(([k, v]) => { mapped[k] = [...v]; });
      setEmpSkillsMap(mapped);
    } catch (e) {
      setErr(e.message || 'Failed to load');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Parent refetch triggered after saves, so join fields stay fresh.
  // We only refetch allocations (cheap) — tree / employees are stable.
  const refreshAllocations = useCallback(async () => {
    try {
      const r = await apiFetch('/api/contract-allocations');
      if (r.ok) {
        const d = await r.json();
        setAllocations(d.allocations || []);
      }
    } catch (_) { /* ignore */ }
  }, []);

  return (
    <div style={{ padding: 20, maxWidth: 1700, margin: '0 auto' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14,
      }}>
        <div style={{ color: '#94A3B8', fontSize: 12.5 }}>
          Assign employees to customer contracts, line items, or sub-items via drag-and-drop.
        </div>
        <button
          onClick={fetchAll}
          style={{
            padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            border: '1px solid rgba(0,224,255,0.35)',
            background: 'rgba(0,224,255,0.08)', color: '#00E0FF', cursor: 'pointer',
          }}
        >Refresh</button>
      </div>

      {err && (
        <div style={{
          background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)',
          color: '#FCA5A5', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13,
        }}>{err}</div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>
          Loading contract allocations…
        </div>
      ) : (
        <ContractDragDropAssign
          employees={employees}
          allocations={allocations}
          contractTree={contractTree}
          skillGroups={skillGroups}
          skillsets={skillsets}
          empSkillsMap={empSkillsMap}
          empSkillsetMap={{}}
          onChange={refreshAllocations}
        />
      )}
    </div>
  );
}
